import { FC, useMemo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormikHelpers, useFormik } from "formik";
import {
  branchInitialValues,
  branchValidationSchema,
  BranchValues,
} from "@/shared/schemas/branch.schema";

import { DialogCreate } from "@/shared/components/molecules/DialogCreate";
import { Select, SelectRuleMax } from "./Select";
import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import { InputFileLarge } from "@/shared/components/atoms/InputFileLarge";
import { InputFile } from "../atoms/InputFile";
import { InputWithLeadingIcon } from "../atoms/InputWithLeadingIcon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAt, faPhone } from "@fortawesome/pro-regular-svg-icons";
import { ToggleText } from "./ToggleText";
import { SelectCountry } from "./SelectCountry";
import { MultiSelect } from "./MultiSelect";
import { InputFilepdf } from "../atoms/InputFilePDF";

import {
  useProductsQuery,
  useContractsQuery,
  useSetsQuery,
} from "@/app/subscription/subscription.api";
import { selectUser } from "@/app/auth/auth.slice";
import { useSelector } from "react-redux";
import { DOMAIN } from "@/app/app.api";
import { ButtonPrimary } from "../atoms/ButtonPrimary";

const calculateDaysFromDate = (dateString: string): number => {
  if (!dateString) return 0;

  const endDate = new Date(dateString);
  const currentDate = new Date();

  // Resetear horas para calcular solo días completos
  currentDate.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);

  const timeDifference = endDate.getTime() - currentDate.getTime();
  const daysDifference = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));

  return Math.max(0, daysDifference); // No permitir números negativos
};

const optionControlAccess = [
  { id: 1, name: "Acceso Fitmewise QR" },
  { id: 2, name: "Acceso IF" },
];

const optionCreacionUsers = [
  { id: 1, name: "Gimnasio" },
  { id: 2, name: "Entrenador Personal" },
];

const optionContabilidad = [
  { id: 1, name: "Ninguna" },
  { id: 2, name: "GTI" },
  { id: 3, name: "SIIGO" },
];

const optionPagos = [
  { id: 1, name: "Ninguna" },
  { id: 2, name: "Stripe" },
  { id: 3, name: "Wompi" },
];

const optionPenalizacion = [
  { id: 0, name: "No" },
  { id: 1, name: "Si" },
];

const optionReservasUsuario = [
  { id: 1, name: "Ilimitado" },
  { id: 2, name: "1" },
  { id: 3, name: "2" },
  { id: 4, name: "3" },
  { id: 5, name: "5" },
  { id: 6, name: "10" },
];

export type BranchFormProps = {
  onSubmit: (
    values: BranchValues,
    formikHelper: FormikHelpers<BranchValues>
  ) => void;
  visible: boolean;
  setVisible: (visible: boolean) => void;
  gyms: Array<any>;
  branch: any;
  countries: Array<any>;
  states: Array<any>;
  timezones: Array<any>;
  isLoadingCreateUpdate?: boolean;
  createMode?: boolean;
};

const transformKeys = (obj: any) => {
  const newObj: any = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const newKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
      newObj[newKey] = obj[key];
    }
  }
  return newObj;
};

export const BranchForm: FC<BranchFormProps> = ({
  onSubmit,
  visible,
  setVisible,
  branch,
  gyms,
  countries,
  states,
  timezones,
  isLoadingCreateUpdate = false,
  createMode = false,
}) => {
  const [t] = useTranslation();

  const initialValues = branch ? transformKeys(branch) : branchInitialValues;
  const optionsMarketing = [
    { id: 1, name: t("brand.views.customize_logo_and_colors") },
    { id: 2, name: t("brand.views.customize_exercise_videos") },
    { id: 3, name: t("posts-master.views.posts_master") },
    //{ id: 4, name: `${t("posts-master.views.posts_master")} Instagram` }, // remove because anyone use it
    { id: 5, name: t("additional-videos.views.title") },
  ];
  const user = useSelector(selectUser);
  const [branchId, setBranchId] = useState(0);

  useEffect(() => {
    if (user) {
      setBranchId(user.branch.id);
    }
  }, [user]);
  const setsQuery = useSetsQuery({
    branchId: branchId > 0 ? branchId : undefined,
  });

  const [setsOptions, setSetsOptions] = useState([]);

  useEffect(() => {
    if (setsQuery.data) {
      setSetsOptions(setsQuery.data);
    }
  }, [setsQuery]);

  const formik = useFormik({
    onSubmit,
    initialValues: {
      ...initialValues,
      gym_id: initialValues.gymId,
      profile_avatar:
        typeof initialValues.logo === "string" && initialValues.logo !== ""
          ? `${DOMAIN}/uploads/branchs/${initialValues.logo}`
          : initialValues.logo,
      large_logo:
        typeof initialValues.largeLogo === "string" &&
        initialValues.largeLogo !== ""
          ? `${DOMAIN}/uploads/branch_large_logo/${initialValues.largeLogo}`
          : initialValues.logo,
      countryCode: initialValues.countryCode,
      first_color: initialValues.firstColor,
      secound_color: initialValues.secoundColor,
      video_tutorial: initialValues.videoTutorial,
      address: initialValues.adress,
      communication: initialValues.communication === 0 ? false : true,
      app_ios: initialValues.appIos,
      app_android: initialValues.appAndroid,
      code_zip: initialValues.codeZip,
      is_chat: initialValues.isChat === 0 ? false : true,
      is_chat_external: initialValues.isChatExternal === 0 ? false : true,
      block_training: initialValues.blockTraining === 0 ? false : true,
      block_nutrition: initialValues.blockNutrition === 0 ? false : true,
      compliance_schedule:
        initialValues.complianceSchedule === 0 ? false : true,
      customer_to_gym_subscription:
        initialValues.customerToGymSubscription === 0 ? false : true,
      multi_center_quotas: initialValues.multiCenterQuotas === 0 ? false : true,
      uses_lesson: initialValues.lessonModule === 0 ? false : true,
      rule_penalty: initialValues.rulePenalty,
      rule_max: initialValues.ruleMax,
      uses_assisteds: initialValues.assistedModule === 0 ? false : true,
      slots: initialValues.slots === 0 ? false : true,
      catchment_module: initialValues.catchmentModule === 0 ? false : true,
      recovery_module: initialValues.recoveryModule === 0 ? false : true,
      ticket_module: initialValues.ticketModule === 0 ? false : true,
      course_module: initialValues.courseModule === 0 ? false : true,
      access_module: initialValues.accessModule === 0 ? false : true,
      access_module_type: Number(initialValues.accessModuleType),
      type_branch: initialValues.typeBranch === "entrenador_personal" ? 2 : 1,
      contract: initialValues.contract,
      available_marketing: [
        initialValues.logoColorModule === 1 ? optionsMarketing[0] : undefined,
        initialValues.videosModule === 1 ? optionsMarketing[1] : undefined,
        initialValues.postModule === 1 ? optionsMarketing[2] : undefined,
        initialValues.additionalVideosModule === 1
          ? optionsMarketing[3]
          : undefined,
      ].filter(Boolean),
      accounting_module: initialValues.accountingModule === 0 ? false : true,
      gti_code: initialValues.gtiCode,
      gti_user: initialValues.gtiUser,
      gti_password: initialValues.gtiPassword,
      gti_terminal: initialValues.gtiTerminal,
      payment_card_module: initialValues.paymentCardModule === 0 ? false : true,
      public_key_payment: initialValues.publicKeyPayment,
      private_key_payment: initialValues.privateKeyPayment,
      prod_integrity: initialValues.prodIntegrity,
      direct_debit_module: initialValues.directDebitModule === 0 ? false : true,
      uses_subscription: initialValues.usesSubscription === 0 ? false : true,
      contract_id: initialValues.contractId,
      subscription_blocking:
        initialValues.subscriptionBlocking === 0 ? false : true,
      available_subscriptions: initialValues.availableSubscriptions,
      trial_period_days: calculateDaysFromDate(
        initialValues.trialPeriodEndDate
      ),
      tax_authorities: initialValues.taxAuthorities,
      gateway: initialValues?.gateway || "Ninguna",
    },
    validationSchema: branchValidationSchema,
    enableReinitialize: true,
  });

  const productsQuery = useProductsQuery({
    branchId: branchId > 0 ? branchId : undefined,
  });

  // Get contracts list
  const contractsQuery = useContractsQuery({
    branchId: branchId ?? undefined,
  });

  const countryOptions = useMemo(() => {
    return countries.map((country) => ({
      ...country,
      code: country.isoCode,
      name: country.name,
    }));
  }, [countries]);

  const stateOptions = useMemo(() => {
    if (!formik.values.countryCode) return [];
    return states
      .filter((state) => state.countryCode == formik.values.countryCode)
      .map((state) => ({
        ...state,
        code: state.isoCode,
      }));
  }, [states, formik.values.countryCode]);

  const timezoneOptions = useMemo(() => {
    if (!formik.values.countryCode) return [];

    return timezones
      ?.find((timezone) => {
        return timezone.code == formik.values.countryCode;
      })
      ?.timezones.map((timezone: any) => ({
        id: timezone.zoneName,
        name: timezone.zoneName,
      }));
  }, [timezones, formik.values.countryCode]);

  return (
    <DialogCreate
      OnclickCreate={formik.handleSubmit}
      OnclickCancel={() => {
        setVisible(false);
        formik.resetForm();
      }}
      onClose={() => {
        setVisible(false);
        formik.resetForm();
      }}
      tittleButtonCreate={createMode ? t("general.create") : t("general.edit")}
      tittleButtonCancel={t("general.cancel")}
      open={visible}
      setOpen={setVisible}
      tittle={t("branch.views.branchs")}
      subTittle={
        createMode
          ? t("branch.views.creating_branchs")
          : t("branch.views.editing_branchs")
      }
      isLoading={isLoadingCreateUpdate}
      acceptDisabled={isLoadingCreateUpdate}
    >
      <div className="mt-6 mx-2">
        <div className="space-y-12">
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
            <div className="flex items-center justify-center h-full text-center">
              <div>
                <h2 className="text-base font-semibold leading-7 text-gray-900">
                  {t("branch.views.profile")}
                </h2>
              </div>
            </div>
            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
              <div className="col-span-full">
                <Select
                  name="gym_id"
                  span={t("general.mandatory")}
                  selected={
                    formik.values.gym_id
                      ? gyms.find(
                          (option) => option.id === formik.values.gym_id
                        )
                      : {}
                  }
                  setSelected={(option) => {
                    formik.setFieldValue("gym_id", option.id);
                  }}
                  options={gyms}
                  tittle={t("branch.views.company")}
                  error={
                    formik?.errors?.gym_id &&
                    (formik?.touched?.gym_id as boolean)
                  }
                  textError={formik?.errors?.gym_id as string}
                />
              </div>
              <div className="col-span-full">
                <InputWithCornerHint
                  label={t("branch.views.name")}
                  span={t("general.mandatory")}
                  id="branch"
                  name="branch"
                  type="text"
                  placeholder={t("branch.views.name_branch")}
                  value={formik.values.branch}
                  onChange={formik.handleChange}
                  error={formik?.errors?.branch && formik?.touched?.branch}
                  textError={formik?.errors?.branch}
                />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
            <div className="flex items-center justify-center h-full text-center">
              <div>
                <h2 className="text-base font-semibold leading-7 text-gray-900">
                  {t("branch.views.personalitation")}
                </h2>
              </div>
            </div>
            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
              <div className="col-span-full">
                <InputFile
                  name="profile_avatar"
                  id="profile_avatar"
                  label={t("branch.views.logo")}
                  spanButton={t("branch.views.upload_image")}
                  description={t("branch.views.description_upload_image")}
                  selectedImage={formik.values.profile_avatar}
                  onFileChange={(file) => {
                    formik.setFieldValue("profile_avatar", file); // Almacena el File original
                  }}
                  onRemoveImage={
                    () => formik.setFieldValue("profile_avatar", null) // Opción para remover la imagen
                  }
                  tittleDelete={t("general.delete_image")}
                />
              </div>
              <div className="col-span-full">
                <InputFileLarge
                  name="large_logo"
                  id="large_logo"
                  label={t("branch.views.large_logo")}
                  spanButton={t("branch.views.upload_image")}
                  description={t("branch.views.description_upload_image")}
                  selectedImage={formik.values.large_logo}
                  onFileChange={(file) => {
                    formik.setFieldValue("large_logo", file); // Almacena el File original
                  }}
                  onRemoveImage={
                    () => formik.setFieldValue("large_logo", null) // Opción para remover la imagen
                  }
                  tittleDelete={t("general.delete_image")}
                />
              </div>
              <div className="col-span-full grid grid-cols-2 gap-8 place-content-center">
                <InputWithCornerHint
                  label={t("branch.views.color_primary")}
                  span={t("general.mandatory")}
                  id="first_color"
                  name="first_color"
                  type="color"
                  value={formik.values.first_color}
                  onChange={formik.handleChange}
                />
                <InputWithCornerHint
                  label={t("branch.views.color_secondary")}
                  span={t("general.mandatory")}
                  id="secound_color"
                  name="secound_color"
                  type="color"
                  value={formik.values.secound_color}
                  onChange={formik.handleChange}
                />
              </div>
              <div className="col-span-full">
                <InputWithCornerHint
                  label={t("branch.views.video_tutorial")}
                  span={t("general.mandatory")}
                  id="video_tutorial"
                  name="video_tutorial"
                  type="url"
                  value={formik.values.video_tutorial}
                  onChange={formik.handleChange}
                  error={
                    formik?.errors?.video_tutorial &&
                    formik?.touched?.video_tutorial
                  }
                  textError={formik?.errors?.video_tutorial as string}
                />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
            <div className="flex items-center justify-center h-full text-center">
              <div>
                <h2 className="text-base font-semibold leading-7 text-gray-900">
                  {t("branch.views.company_dates")}
                </h2>
              </div>
            </div>
            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
              <div className="col-span-full">
                <InputWithLeadingIcon
                  label={t("branch.views.phone")}
                  span={t("general.mandatory")}
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder={t("branch.views.phone")}
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  error={
                    formik?.errors?.phone && (formik?.touched?.phone as boolean)
                  }
                  textError={formik?.errors?.phone as string}
                  icon={
                    <FontAwesomeIcon
                      icon={faPhone}
                      className="h-4 w-4 text-gray-400"
                    />
                  }
                />
              </div>
              <div className="col-span-full">
                <InputWithLeadingIcon
                  label={t("branch.views.email")}
                  span={t("general.mandatory")}
                  id="email"
                  name="email"
                  type="email"
                  placeholder={t("branch.views.email")}
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  error={
                    formik?.errors?.email && (formik?.touched?.email as boolean)
                  }
                  textError={formik?.errors?.email as string}
                  icon={
                    <FontAwesomeIcon
                      icon={faAt}
                      className="h-4 w-4 text-gray-400"
                    />
                  }
                />
              </div>
              <div className="col-span-full">
                <SelectCountry
                  span={t("general.mandatory")}
                  selected={
                    formik?.values?.country
                      ? countryOptions.find(
                          (option) => option.id === formik.values.country
                        )
                      : {}
                  }
                  setSelected={(option) => {
                    formik.setFieldValue("country", option.id);
                    formik.setFieldValue("countryCode", option.code);
                  }}
                  options={countryOptions}
                  tittle={t("branch.views.country")}
                />
              </div>

              <div className="col-span-full">
                <Select
                  name="state"
                  selected={stateOptions.find(
                    (state) => state.code == formik.values.stateCode
                  )}
                  setSelected={(option: any) => {
                    console.log({ option });
                    formik.setFieldValue("state", option.id);
                    formik.setFieldValue("stateCode", option.code);
                  }}
                  options={stateOptions}
                  tittle={t("branch.views.state_prov")}
                  disabled={stateOptions.length === 0}
                />
              </div>
              <div className="col-span-full">
                <InputWithCornerHint
                  label={t("branch.views.population")}
                  span={t("general.mandatory")}
                  id="population"
                  name="population"
                  type="text"
                  placeholder={t("branch.views.population")}
                  onChange={formik.handleChange}
                  value={formik.values.population}
                  error={
                    formik?.errors?.population &&
                    (formik?.touched?.population as boolean)
                  }
                  textError={formik?.errors?.population as string}
                />
              </div>
              <div className="col-span-full">
                <InputWithCornerHint
                  label={t("branch.views.adress")}
                  span={t("general.mandatory")}
                  id="address"
                  name="address"
                  type="text"
                  placeholder={t("branch.views.adress")}
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  error={
                    formik?.errors?.address &&
                    (formik?.touched?.address as boolean)
                  }
                  textError={formik?.errors?.address as string}
                />
              </div>
              <div className="col-span-full">
                <InputWithCornerHint
                  label={t("branch.views.code_zip")}
                  span={t("general.mandatory")}
                  id="code_zip"
                  name="code_zip"
                  type="text"
                  placeholder={t("branch.views.code_zip")}
                  value={formik.values.code_zip}
                  onChange={formik.handleChange}
                  error={
                    formik?.errors?.code_zip &&
                    (formik?.touched?.code_zip as boolean)
                  }
                  textError={formik?.errors?.code_zip as string}
                />
              </div>
              <div className="col-span-full">
                <Select
                  name="timezone"
                  selected={timezoneOptions?.find(
                    (timezone) => timezone.id == formik.values.timezone
                  )}
                  setSelected={(option) =>
                    formik.setFieldValue("timezone", option.id)
                  }
                  options={timezoneOptions}
                  tittle={t("branch.views.timezone")}
                />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
            <div className="flex items-center justify-center h-full text-center">
              <div>
                <h2 className="text-base font-semibold leading-7 text-gray-900">
                  {t("branch.views.title_app")}
                </h2>
              </div>
            </div>
            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
              <div className="col-span-full">
                <div className="relative flex items-start">
                  <div className="flex h-6 items-center">
                    <input
                      checked={formik.values.communication}
                      onChange={() => {
                        formik.setFieldValue(
                          "communication",
                          !formik.values.communication
                        );
                      }}
                      id="communication"
                      name="communication"
                      type="checkbox"
                      aria-describedby="communication"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                    />
                  </div>
                  <div className="ml-3 text-sm leading-6">
                    <label
                      htmlFor="appFitmewise"
                      className="font-medium text-gray-900"
                    >
                      {t("branch.views.app_fitmewise")}
                    </label>
                  </div>
                </div>
              </div>
              {formik.values.communication === false ? (
                <>
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("branch.views.ios_app")}
                      span={t("general.optional")}
                      id="app_ios"
                      name="app_ios"
                      type="url"
                      placeholder="http://app.com"
                      value={formik.values.app_ios}
                      onChange={formik.handleChange}
                      error={formik.errors.app_ios}
                    />
                  </div>
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("branch.views.android_app")}
                      span={t("general.optional")}
                      id="app_android"
                      name="app_android"
                      type="url"
                      placeholder="http://privacy.com"
                      value={formik.values.app_android}
                      onChange={formik.handleChange}
                      error={formik.errors.app_android}
                    />
                  </div>
                </>
              ) : (
                <></>
              )}
              <div className="col-span-full">
                <div className="relative flex items-start">
                  <div className="flex h-6 items-center">
                    <input
                      checked={formik.values.is_chat}
                      onChange={() => {
                        formik.setFieldValue("is_chat", !formik.values.is_chat);
                      }}
                      id="is_chat"
                      name="is_chat"
                      type="checkbox"
                      aria-describedby="is_chat"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                    />
                  </div>
                  <div className="ml-3 text-sm leading-6">
                    <label
                      htmlFor="is_chat"
                      className="font-medium text-gray-900"
                    >
                      {t("branch.views.chat")}
                    </label>
                  </div>
                </div>
              </div>
              {formik.values.is_chat ? (
                <>
                  <div className="col-span-full">
                    <ToggleText
                      checked={formik.values.is_chat_external}
                      onChange={() => {
                        formik.setFieldValue(
                          "is_chat_external",
                          !formik.values.is_chat_external
                        );
                      }}
                      texto={
                        formik.values.is_chat_external === false
                          ? t("branch.views.branch")
                          : "Fitmewise"
                      }
                    />
                  </div>
                  {formik.values.is_chat_external === false ? (
                    <div className="col-span-full">
                      <Select
                        selected={formik.values.sets}
                        setSelected={(option: any) => {
                          formik.setFieldValue("sets", option.id);
                        }}
                        options={setsOptions}
                        tittle={"Sets"}
                      />
                    </div>
                  ) : null}
                </>
              ) : null}
              <div className="col-span-full">
                <div className="relative flex items-start">
                  <div className="flex h-6 items-center">
                    <input
                      id="block_training"
                      name="block_training"
                      type="checkbox"
                      aria-describedby="block_training"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                      checked={formik.values.block_training}
                      onChange={() => {
                        formik.setFieldValue(
                          "block_training",
                          !formik.values.block_training
                        );
                      }}
                    />
                  </div>
                  <div className="ml-3 text-sm leading-6">
                    <label
                      htmlFor="block_training"
                      className="font-medium text-gray-900"
                    >
                      {t("branch.views.block_training")}
                    </label>
                  </div>
                </div>
              </div>
              <div className="col-span-full">
                <div className="relative flex items-start">
                  <div className="flex h-6 items-center">
                    <input
                      id="block_nutrition"
                      name="block_nutrition"
                      type="checkbox"
                      aria-describedby="block_nutrition"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                      checked={formik.values.block_nutrition}
                      onChange={() => {
                        formik.setFieldValue(
                          "block_nutrition",
                          !formik.values.block_nutrition
                        );
                      }}
                    />
                  </div>
                  <div className="ml-3 text-sm leading-6">
                    <label
                      htmlFor="block_nutrition"
                      className="font-medium text-gray-900"
                    >
                      {t("branch.views.block_nutrition")}
                    </label>
                  </div>
                </div>
              </div>
              <div className="col-span-full">
                <div className="relative flex items-start">
                  <div className="flex h-6 items-center">
                    <input
                      id="compliance_schedule"
                      name="compliance_schedule"
                      type="checkbox"
                      aria-describedby="compliance_schedule"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                      checked={formik.values.compliance_schedule}
                      onChange={() => {
                        formik.setFieldValue(
                          "compliance_schedule",
                          !formik.values.compliance_schedule
                        );
                      }}
                    />
                  </div>
                  <div className="ml-3 text-sm leading-6">
                    <label
                      htmlFor="compliance_schedule"
                      className="font-medium text-gray-900"
                    >
                      {t("branch.views.uses_compliance_schedule")}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
            <div className="flex items-center justify-center h-full text-center">
              <div>
                <h2 className="text-base font-semibold leading-7 text-gray-900">
                  {t("branch.views.title_dashboard")}
                </h2>
              </div>
            </div>
            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
              <div className="col-span-full">
                <div className="relative flex items-start">
                  <div className="flex h-6 items-center">
                    <input
                      checked={formik.values.customer_to_gym_subscription}
                      onChange={() => {
                        formik.setFieldValue(
                          "customer_to_gym_subscription",
                          !formik.values.customer_to_gym_subscription
                        );
                      }}
                      id="customer_to_gym_subscription"
                      name="customer_to_gym_subscription"
                      type="checkbox"
                      aria-describedby="customer_to_gym_subscription"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                    />
                  </div>
                  <div className="ml-3 text-sm leading-6">
                    <label
                      htmlFor="customer_to_gym_subscription"
                      className="font-medium text-gray-900"
                    >
                      {t("branch.views.uses_fee")}
                    </label>
                  </div>
                </div>
              </div>
              {formik.values.customer_to_gym_subscription === true ? (
                <div className="col-span-full">
                  <div className="relative flex items-start">
                    <div className="flex h-6 items-center">
                      <input
                        id="multi_center_quotas"
                        name="multi_center_quotas"
                        type="checkbox"
                        aria-describedby="multi_center_quotas"
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                        checked={formik.values.multi_center_quotas}
                        onChange={() => {
                          formik.setFieldValue(
                            "multi_center_quotas",
                            !formik.values.multi_center_quotas
                          );
                        }}
                      />
                    </div>
                    <div className="ml-3 text-sm leading-6">
                      <label
                        htmlFor="multi_center_quotas"
                        className="font-medium text-gray-900"
                      >
                        {t("branch.views.multi-center_quotas")}
                      </label>
                    </div>
                  </div>
                </div>
              ) : null}
              <div className="col-span-full">
                <div className="relative flex items-start">
                  <div className="flex h-6 items-center">
                    <input
                      id="uses_lesson"
                      name="uses_lesson"
                      type="checkbox"
                      aria-describedby="uses_lesson"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                      checked={formik.values.uses_lesson}
                      onChange={() => {
                        formik.setFieldValue(
                          "uses_lesson",
                          !formik.values.uses_lesson
                        );
                      }}
                    />
                  </div>
                  <div className="ml-3 text-sm leading-6">
                    <label
                      htmlFor="uses_lesson"
                      className="font-medium text-gray-900"
                    >
                      {t("branch.views.check")}
                    </label>
                  </div>
                </div>
              </div>
              {formik.values.uses_lesson === true ? (
                <>
                  <div className="col-span-full">
                    <Select
                      addDefault={false}
                      selected={optionPenalizacion.find(
                        (option) => option.id === +formik.values.rule_penalty
                      )}
                      setSelected={(option) => {
                        formik.setFieldValue("rule_penalty", option.id);
                      }}
                      options={optionPenalizacion}
                      tittle={t("branch.views.rule_1")}
                      textDescribe={t("branch.views.rule_1_desc")}
                    />
                  </div>
                  <div className="col-span-full">
                    <SelectRuleMax
                      value={formik.values.rule_max}
                      onChange={(value) => {
                        formik.setFieldValue("rule_max", value);
                      }}
                      options={optionReservasUsuario}
                    />
                  </div>
                </>
              ) : null}
              <div className="col-span-full">
                <div className="relative flex items-start">
                  <div className="flex h-6 items-center">
                    <input
                      id="uses_assisteds"
                      name="uses_assisteds"
                      type="checkbox"
                      aria-describedby="uses_assisteds"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                      checked={formik.values.uses_assisteds}
                      onChange={() => {
                        formik.setFieldValue(
                          "uses_assisteds",
                          !formik.values.uses_assisteds
                        );
                      }}
                    />
                  </div>
                  <div className="ml-3 text-sm leading-6">
                    <label
                      htmlFor="uses_assisteds"
                      className="font-medium text-gray-900"
                    >
                      {t("branch.views.check_services")}
                    </label>
                  </div>
                </div>
              </div>
              <div className="col-span-full">
                <div className="relative flex items-start">
                  <div className="flex h-6 items-center">
                    <input
                      id="slots"
                      name="slots"
                      type="checkbox"
                      aria-describedby="slots"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                      checked={formik.values.slots}
                      onChange={() => {
                        formik.setFieldValue("slots", !formik.values.slots);
                      }}
                    />
                  </div>
                  <div className="ml-3 text-sm leading-6">
                    <label
                      htmlFor="slots"
                      className="font-medium text-gray-900"
                    >
                      {t("branch.views.check_reservas")}
                    </label>
                  </div>
                </div>
              </div>
              <div className="col-span-full">
                <div className="relative flex items-start">
                  <div className="flex h-6 items-center">
                    <input
                      id="catchment_module"
                      name="catchment_module"
                      type="checkbox"
                      aria-describedby="catchment_module"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                      checked={formik.values.catchment_module}
                      onChange={() => {
                        formik.setFieldValue(
                          "catchment_module",
                          !formik.values.catchment_module
                        );
                      }}
                    />
                  </div>
                  <div className="ml-3 text-sm leading-6">
                    <label
                      htmlFor="catchment_module"
                      className="font-medium text-gray-900"
                    >
                      {t("branch.views.check_captation")}
                    </label>
                  </div>
                </div>
              </div>
              <div className="col-span-full">
                <div className="relative flex items-start">
                  <div className="flex h-6 items-center">
                    <input
                      id="recovery_module"
                      name="recovery_module"
                      type="checkbox"
                      aria-describedby="recovery_module"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                      checked={formik.values.recovery_module}
                      onChange={() => {
                        formik.setFieldValue(
                          "recovery_module",
                          !formik.values.recovery_module
                        );
                      }}
                    />
                  </div>
                  <div className="ml-3 text-sm leading-6">
                    <label
                      htmlFor="recovery_module"
                      className="font-medium text-gray-900"
                    >
                      {t("branch.views.check_recuperacion")}
                    </label>
                  </div>
                </div>
              </div>
              <div className="col-span-full">
                <div className="relative flex items-start">
                  <div className="flex h-6 items-center">
                    <input
                      id="ticket_module"
                      name="ticket_module"
                      type="checkbox"
                      aria-describedby="ticket_module"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                      checked={formik.values.ticket_module}
                      onChange={() => {
                        formik.setFieldValue(
                          "ticket_module",
                          !formik.values.ticket_module
                        );
                      }}
                    />
                  </div>
                  <div className="ml-3 text-sm leading-6">
                    <label
                      htmlFor="ticket_module"
                      className="font-medium text-gray-900"
                    >
                      {t("branch.views.check_assistant")}
                    </label>
                  </div>
                </div>
              </div>
              <div className="col-span-full">
                <div className="relative flex items-start">
                  <div className="flex h-6 items-center">
                    <input
                      id="store"
                      name="store"
                      type="checkbox"
                      aria-describedby="store"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                      checked={formik.values.store}
                      onChange={() => {
                        formik.setFieldValue("store", !formik.values.store);
                      }}
                    />
                  </div>
                  <div className="ml-3 text-sm leading-6">
                    <label
                      htmlFor="store"
                      className="font-medium text-gray-900"
                    >
                      {t("branch.views.check_shop")}
                    </label>
                  </div>
                </div>
              </div>
              <div className="col-span-full">
                <div className="relative flex items-start">
                  <div className="flex h-6 items-center">
                    <input
                      id="course_module"
                      name="course_module"
                      type="checkbox"
                      aria-describedby="course_module"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                      checked={formik.values.course_module}
                      onChange={() => {
                        formik.setFieldValue(
                          "course_module",
                          !formik.values.course_module
                        );
                      }}
                    />
                  </div>
                  <div className="ml-3 text-sm leading-6">
                    <label
                      htmlFor="course_module"
                      className="font-medium text-gray-900"
                    >
                      {t("branch.views.check_cursos")}
                    </label>
                  </div>
                </div>
              </div>
              <div className="col-span-full">
                <div className="relative flex items-start">
                  <div className="flex h-6 items-center">
                    <input
                      id="access_module"
                      name="access_module"
                      type="checkbox"
                      aria-describedby="access_module"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                      checked={formik.values.access_module}
                      onChange={() => {
                        formik.setFieldValue(
                          "access_module",
                          !formik.values.access_module
                        );
                      }}
                    />
                  </div>
                  <div className="ml-3 text-sm leading-6">
                    <label
                      htmlFor="access_module"
                      className="font-medium text-gray-900"
                    >
                      {t("branch.views.check_access")}
                    </label>
                  </div>
                </div>
              </div>
              {formik.values.access_module ? (
                <div className="col-span-full">
                  <Select
                    selected={optionControlAccess.find(
                      (option) => option.id === formik.values.access_module_type
                    )}
                    setSelected={(option) => {
                      formik.setFieldValue("access_module_type", option.id);
                    }}
                    options={optionControlAccess}
                    tittle={t("branch.views.access")}
                  />
                </div>
              ) : null}
              <div className="col-span-full">
                <Select
                  selected={optionCreacionUsers.find(
                    (option) => option.id === formik.values.type_branch
                  )}
                  setSelected={(option) => {
                    formik.setFieldValue("type_branch", option.id);
                  }}
                  options={optionCreacionUsers}
                  tittle={t("branch.views.type")}
                />
              </div>
              {formik.values.type_branch === 1 ? (
                <div className="col-span-full">
                  <InputFilepdf
                    name="contract"
                    id="contract"
                    label={t("share.views.Subir_Reemplazar_contrato")}
                    span=""
                    spanButton={t("share.views.up_contracto")}
                    description={t("general.format_pdf")}
                    //selectedFileName={formik?.values?.contract}
                    selectedFileName={
                      formik.values.contract instanceof File
                        ? formik.values.contract.name
                        : formik.values.contract
                    }
                    error={
                      formik?.errors?.contract &&
                      (formik?.touched?.contract as boolean)
                    }
                    onFileChange={(file) => {
                      //const contractURL = URL.createObjectURL(file);
                      formik.setFieldValue("contract", file);
                    }}
                    onRemoveFile={() => {
                      formik.setFieldValue("contract", null);
                    }}
                    tittleDelete={t("general.delete_archive")}
                  />

                  {!(formik.values.contract instanceof File) &&
                  formik.values.contract ? (
                    <div className="flex flex-1 items-center justify-center">
                      <ButtonPrimary
                        text="View contract"
                        onClick={() => {
                          window.open(`${formik.values.contract}`);
                        }}
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}
              <div className="col-span-full">
                <MultiSelect
                  options={optionsMarketing}
                  selectedOptions={formik.values.available_marketing.filter(
                    (option) => Boolean(option)
                  )}
                  setSelectedOptions={(options) => {
                    const selectedIds = Array.isArray(options)
                      ? options
                          .map((option) => option)
                          .filter((option) => Boolean(option?.id))
                      : [];
                    formik.setFieldValue("available_marketing", selectedIds);
                  }}
                  title={t("branch.views.marketing")}
                />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
            <div className="flex items-center justify-center h-full text-center">
              <div>
                <h2 className="text-base font-semibold leading-7 text-gray-900">
                  {t("branch.views.title_method_payment")}
                </h2>
              </div>
            </div>
            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
              <div className="col-span-full">
                <div className="relative flex items-start">
                  <div className="flex h-6 items-center">
                    <input
                      checked={formik.values.accounting_module}
                      onChange={() => {
                        formik.setFieldValue(
                          "accounting_module",
                          !formik.values.accounting_module
                        );
                      }}
                      id="accounting_module"
                      name="accounting_module"
                      type="checkbox"
                      aria-describedby="accounting_module"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                    />
                  </div>
                  <div className="ml-3 text-sm leading-6">
                    <label
                      htmlFor="accounting_module"
                      className="font-medium text-gray-900"
                    >
                      {t("branch.views.check_contabilidad")}
                    </label>
                  </div>
                </div>
              </div>
              {formik.values.accounting_module ? (
                <div className="col-span-full">
                  <Select
                    span={t("general.mandatory")}
                    selected={optionContabilidad.find(
                      (option) => option.name === formik.values.tax_authorities
                    )}
                    setSelected={(option) => {
                      formik.setFieldValue("tax_authorities", option.name);
                    }}
                    options={optionContabilidad}
                    tittle={t("branch.views.tax_authorities")}
                  />
                </div>
              ) : null}
              {formik.values.tax_authorities === "GTI" ? (
                <>
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("branch.views.code_activation")}
                      span={t("general.mandatory")}
                      id="gti_code"
                      name="gti_code"
                      type="text"
                      onChange={formik.handleChange}
                      value={formik.values.gti_code}
                      error={
                        formik?.errors?.gti_code &&
                        (formik?.touched?.gti_code as boolean)
                      }
                      textError={formik?.errors?.gti_code as string}
                    />
                  </div>
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("branch.views.user")}
                      span={t("general.mandatory")}
                      id="gti_user"
                      name="gti_user"
                      type="text"
                      placeholder={t("branch.views.user")}
                      onChange={formik.handleChange}
                      value={formik.values.gti_user}
                      error={
                        formik?.errors?.gti_user &&
                        (formik?.touched?.gti_user as boolean)
                      }
                      textError={formik?.errors?.gti_user as string}
                    />
                  </div>
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("branch.views.pass")}
                      span={t("general.mandatory")}
                      id="gti_password"
                      name="gti_password"
                      type="text"
                      placeholder={t("branch.views.pass")}
                      onChange={formik.handleChange}
                      value={formik.values.gti_password}
                      error={
                        formik?.errors?.gti_password &&
                        (formik?.touched?.gti_password as boolean)
                      }
                      textError={formik?.errors?.gti_password as string}
                    />
                  </div>
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("branch.views.terminal")}
                      span={t("general.mandatory")}
                      id="gti_terminal"
                      name="gti_terminal"
                      type="text"
                      placeholder={t("branch.views.terminal")}
                      onChange={formik.handleChange}
                      value={formik.values.gti_terminal}
                      error={
                        formik?.errors?.gti_terminal &&
                        (formik?.touched?.gti_terminal as boolean)
                      }
                      textError={formik?.errors?.gti_terminal as string}
                    />
                  </div>
                </>
              ) : formik.values.tax_authorities === "SIIGO" ? (
                <>
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("branch.views.user")}
                      span={t("general.mandatory")}
                      id="gti_user"
                      name="gti_user"
                      type="text"
                      placeholder={t("branch.views.user")}
                      onChange={formik.handleChange}
                      value={formik.values.gti_user}
                      error={
                        formik?.errors?.gti_user &&
                        (formik?.touched?.gti_user as boolean)
                      }
                      textError={formik?.errors?.gti_user as string}
                    />
                  </div>
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("branch.views.pass")}
                      span={t("general.mandatory")}
                      id="gti_password"
                      name="gti_password"
                      type="text"
                      placeholder={t("branch.views.pass")}
                      onChange={formik.handleChange}
                      value={formik.values.gti_password}
                      error={
                        formik?.errors?.gti_password &&
                        (formik?.touched?.gti_password as boolean)
                      }
                      textError={formik?.errors?.gti_password as string}
                    />
                  </div>
                </>
              ) : null}
              <div className="col-span-full">
                <div className="relative flex items-start">
                  <div className="flex h-6 items-center">
                    <input
                      id="payment_card_module"
                      name="payment_card_module"
                      type="checkbox"
                      aria-describedby="payment_card_module"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                      checked={formik.values.payment_card_module}
                      onChange={() => {
                        formik.setFieldValue(
                          "payment_card_module",
                          !formik.values.payment_card_module
                        );
                      }}
                    />
                  </div>
                  <div className="ml-3 text-sm leading-6">
                    <label
                      htmlFor="payment_card_module"
                      className="font-medium text-gray-900"
                    >
                      {t("branch.views.check_paymentOnline")}
                    </label>
                  </div>
                </div>
              </div>
              {formik.values.payment_card_module ? (
                <>
                  <div className="col-span-full">
                    <Select
                      addDefault={false}
                      selected={optionPagos.find(
                        (option) =>
                          option?.name?.toLowerCase() ===
                          formik.values.gateway?.toLowerCase()
                      )}
                      setSelected={(option) => {
                        formik.setFieldValue(
                          "gateway",
                          option.name.toLowerCase()
                        );
                      }}
                      options={optionPagos}
                      tittle={t("branch.views.payment_gateway")}
                    />
                  </div>

                  {formik.values.gateway === "stripe" ||
                  formik.values.gateway === "wompi" ? (
                    <>
                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("branch.views.public_key_payment")}
                          span={t("general.mandatory")}
                          id="public_key_payment"
                          name="public_key_payment"
                          type="text"
                          placeholder={t("branch.views.public_key_payment")}
                          onChange={formik.handleChange}
                          value={formik.values.public_key_payment}
                          error={
                            formik?.errors?.public_key_payment &&
                            (formik?.touched?.public_key_payment as boolean)
                          }
                          textError={
                            formik?.errors?.public_key_payment as string
                          }
                        />
                      </div>
                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("branch.views.private_key_payment")}
                          span={t("general.mandatory")}
                          id="private_key_payment"
                          name="private_key_payment"
                          type="text"
                          placeholder={t("branch.views.private_key_payment")}
                          onChange={formik.handleChange}
                          value={formik.values.private_key_payment}
                          error={
                            formik?.errors?.private_key_payment &&
                            (formik?.touched?.private_key_payment as boolean)
                          }
                          textError={
                            formik?.errors?.private_key_payment as string
                          }
                        />
                      </div>
                      {formik.values.gateway === "wompi" ? (
                        <>
                          <div className="col-span-full">
                            <InputWithCornerHint
                              label={t("branch.views.prod_integrity")}
                              span={t("general.mandatory")}
                              id="prod_integrity"
                              name="prod_integrity"
                              type="text"
                              placeholder={t(
                                "branch.views.private_key_payment"
                              )}
                              onChange={formik.handleChange}
                              value={formik.values.prod_integrity}
                              error={
                                formik?.errors?.prod_integrity &&
                                (formik?.touched?.prod_integrity as boolean)
                              }
                              textError={
                                formik?.errors?.prod_integrity as string
                              }
                            />
                          </div>
                        </>
                      ) : (
                        <></>
                      )}
                    </>
                  ) : (
                    <></>
                  )}
                </>
              ) : null}
              <div className="col-span-full">
                <div className="relative flex items-start">
                  <div className="flex h-6 items-center">
                    <input
                      id="direct_debit_module"
                      name="direct_debit_module"
                      type="checkbox"
                      aria-describedby="direct_debit_module"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                      checked={formik.values.direct_debit_module}
                      onChange={() => {
                        formik.setFieldValue(
                          "direct_debit_module",
                          !formik.values.direct_debit_module
                        );
                      }}
                    />
                  </div>
                  <div className="ml-3 text-sm leading-6">
                    <label
                      htmlFor="direct_debit_module"
                      className="font-medium text-gray-900"
                    >
                      {t("branch.views.uses_direct_debit")}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
            <div className="flex items-center justify-center h-full text-center">
              <div>
                <h2 className="text-base font-semibold leading-7 text-gray-900">
                  {t("branch.views.uses_fmw_subscription")}
                </h2>
              </div>
            </div>
            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
              <div className="col-span-full">
                <div className="relative flex items-start">
                  <div className="flex h-6 items-center">
                    <input
                      checked={formik.values.uses_subscription}
                      onChange={() => {
                        formik.setFieldValue(
                          "uses_subscription",
                          !formik.values.uses_subscription
                        );
                      }}
                      id="uses_subscription"
                      name="uses_subscription"
                      type="checkbox"
                      aria-describedby="uses_subscription"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                    />
                  </div>
                  <div className="ml-3 text-sm leading-6">
                    <label
                      htmlFor="uses_subscription"
                      className="font-medium text-gray-900"
                    >
                      {t("branch.views.uses_fmw_subscription_description")}
                    </label>
                  </div>
                </div>
              </div>
              {formik.values.uses_subscription && (
                <>
                  <div className="col-span-full">
                    <MultiSelect
                      span={t("general.optional")}
                      options={productsQuery?.data ?? []}
                      selectedOptions={formik.values.available_subscriptions}
                      setSelectedOptions={(selected) => {
                        formik.setFieldValue(
                          "available_subscriptions",
                          Array.isArray(selected)
                            ? selected.map((subscription) => ({
                                id: subscription.id,
                                name: subscription.name,
                              }))
                            : {
                                id: selected.id,
                                name: selected.name,
                              }
                        );
                      }}
                      title={t("branch.views.subscriptions")}
                    />
                  </div>
                  <div className="col-span-full">
                    <Select
                      addDefault={false}
                      span={t("general.optional")}
                      selected={contractsQuery?.data?.find(
                        (option) => option.id == formik.values.contract_id
                      )}
                      setSelected={(option) => {
                        formik.setFieldValue("contract_id", option.id);
                      }}
                      options={contractsQuery.data}
                      tittle={t("branch.views.contracts_available")}
                      error={
                        formik.errors.contract_id &&
                        (formik.touched.contract_id as boolean)
                      }
                      textError={formik.errors.contract_id as string}
                    />
                  </div>
                  <div className="col-span-full">
                    <div className="relative flex items-start">
                      <div className="flex h-6 items-center">
                        <input
                          checked={formik.values.subscription_blocking}
                          onChange={() => {
                            formik.setFieldValue(
                              "subscription_blocking",
                              !formik.values.subscription_blocking
                            );
                          }}
                          id="subscription_blocking"
                          name="subscription_blocking"
                          type="checkbox"
                          aria-describedby="subscription_blocking"
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                        />
                      </div>
                      <div className="ml-3 text-sm leading-6">
                        <label
                          htmlFor="subscription_blocking"
                          className="font-medium text-gray-900"
                        >
                          {t("branch.views.subscription_blocking_description")}
                        </label>
                      </div>
                    </div>
                  </div>
                  {formik.values.subscription_blocking && (
                    <div className="col-span-full">
                      <InputWithCornerHint
                        label={t("branch.views.trial_period_end_date")}
                        span={t("general.optional")}
                        id="trial_period_days"
                        name="trial_period_days"
                        type="number"
                        placeholder={t("branch.views.trial_period_end_date")}
                        value={formik.values.trial_period_days}
                        onChange={formik.handleChange}
                        error={
                          formik.errors.trial_period_days &&
                          (formik.touched.trial_period_days as boolean)
                        }
                        textError={formik.errors.trial_period_days as string}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </DialogCreate>
  );
};
